import {
  ButtonIcon,
  FileDropzone,
  FloatingDeleteButton,
  FormField,
  FormSection,
  FormSelect,
  PrimaryButton,
} from '@/components';
import { Sidepanel } from '@/components/layout/Sidepanel';
import {
  SidebarContent,
  SidebarFooter,
} from '@/components/layout/sidepanel/SidebarFooter';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  useCreateRepair,
  useDeleteRepair,
  useUpdateRepair,
  useUploadRepairDocument,
} from '../hooks';
import {
  REPAIR_STATES,
  type RepairHistoryEntry,
  type RepairState,
  type UpdateRepairRequest,
} from '../types';
import { openRepairDocument } from './openRepairDocument';

interface RepairHistorySidePanelProps {
  deviceId: string;
  // The entry being edited, or null to add a new one.
  entry: RepairHistoryEntry | null;
  isOpen: boolean;
  panelKey: string | null;
  onClose: () => void;
}

// RepairHistorySidePanel hosts the add and edit forms, which share one layout and differ only
// in create-vs-update on Save. The panelKey remounts the content so fields reset on each open.
export function RepairHistorySidePanel({
  deviceId,
  entry,
  isOpen,
  panelKey,
  onClose,
}: RepairHistorySidePanelProps) {
  const { t } = useTranslation();
  const title = entry
    ? t('repairHistory.sidePanel.editTitle')
    : t('repairHistory.sidePanel.addTitle');

  return (
    <Sidepanel key={panelKey ?? undefined} isOpen={isOpen} onClose={onClose} title={title}>
      <RepairHistoryForm deviceId={deviceId} entry={entry} onDone={onClose} />
    </Sidepanel>
  );
}

function RepairHistoryForm({
  deviceId,
  entry,
  onDone,
}: {
  deviceId: string;
  entry: RepairHistoryEntry | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const createRepair = useCreateRepair(deviceId);
  const updateRepair = useUpdateRepair(deviceId);
  const deleteRepair = useDeleteRepair(deviceId);
  const uploadDocument = useUploadRepairDocument(deviceId);

  const [name, setName] = useState(entry?.name ?? '');
  const [date, setDate] = useState(entry?.date ?? '');
  const [state, setState] = useState<RepairState>(entry?.state ?? REPAIR_STATES[0]);
  const [file, setFile] = useState<File | null>(null);
  // null while idle; 0-100 drives the loading bar during an active upload.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const isSubmitting =
    createRepair.isPending || updateRepair.isPending || uploadDocument.isPending;
  const canSubmit = Boolean(name && date) && !isSubmitting;

  // handleSave creates or updates the entry, then uploads the chosen document (exchanging the
  // previous one on edit). Nothing is applied until Save is pressed.
  const handleSave = async () => {
    const onProgress = (progress: number) => setUploadProgress(progress);

    if (entry) {
      const changed = collectChangedFields(entry, { name, date, state });
      if (Object.keys(changed).length > 0) {
        await updateRepair.mutateAsync({ repairId: entry.id, request: changed });
      }
      if (file) {
        setUploadProgress(0);
        await uploadDocument.mutateAsync({ repairId: entry.id, file, onProgress });
      }
    } else {
      const created = await createRepair.mutateAsync({ deviceId, date, state, name });
      if (file) {
        setUploadProgress(0);
        await uploadDocument.mutateAsync({ repairId: created.id, file, onProgress });
      }
    }

    onDone();
  };

  const handleDelete = async () => {
    if (!entry) return;
    await deleteRepair.mutateAsync(entry.id);
    onDone();
  };

  return (
    <>
      <SidebarContent className="space-y-5 py-4">
        <FormSection icon="wrench" title={t('repairHistory.sidePanel.section')}>
          <FormSelect
            label={t('repairHistory.fields.state')}
            value={state}
            onChange={(event) => setState(event.target.value as RepairState)}
          >
            {REPAIR_STATES.map((option) => (
              <option key={option} value={option}>
                {t(`repairHistory.state.${option}`)}
              </option>
            ))}
          </FormSelect>
          <FormField
            label={t('repairHistory.fields.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            mandatory
          />
          <FormField
            label={t('repairHistory.fields.date')}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            mandatory
          />
        </FormSection>

        <RepairDocumentSection
          entry={entry}
          file={file}
          onFileSelect={setFile}
          disabled={isSubmitting}
          uploadProgress={uploadProgress}
        />
      </SidebarContent>

      <SidebarFooter className="flex justify-between">
        {entry ? (
          <FloatingDeleteButton
            onDelete={handleDelete}
            isDeleting={deleteRepair.isPending}
            disabled={isSubmitting}
            modalTitle={t('repairHistory.confirm.delete.title')}
            modalMessage={t('repairHistory.confirm.delete.message')}
          />
        ) : (
          <span />
        )}
        <PrimaryButton
          type="button"
          onClick={handleSave}
          disabled={!canSubmit}
          isLoading={isSubmitting}
        >
          {t('repairHistory.actions.save')}
        </PrimaryButton>
      </SidebarFooter>
    </>
  );
}

// RepairDocumentSection holds the document controls and the upload progress bar shown while a
// document is uploading. When a document already exists, the Open icon sits next to a
// "Replace document" dropzone (side by side on wider screens, stacked on small ones).
function RepairDocumentSection({
  entry,
  file,
  onFileSelect,
  disabled,
  uploadProgress,
}: {
  entry: RepairHistoryEntry | null;
  file: File | null;
  onFileSelect: (file: File | null) => void;
  disabled: boolean;
  uploadProgress: number | null;
}) {
  const { t } = useTranslation();
  const canOpenExisting = Boolean(entry?.documentAvailable);
  const isUploading = uploadProgress !== null;

  const handleOpen = () => {
    if (!entry) return;
    void openRepairDocument(entry.id, () =>
      toast.error(t('repairHistory.toast.openError'))
    );
  };

  return (
    <FormSection icon="documents" title={t('repairHistory.fields.document')}>
      {canOpenExisting ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <ButtonIcon
            icon="download"
            ariaLabel={t('repairHistory.actions.open')}
            onClick={handleOpen}
            disabled={disabled}
          />
          <div className="flex-1">
            <FileDropzone
              file={file}
              onFileSelect={onFileSelect}
              disabled={disabled}
              accept=".pdf"
              prompt={t('repairHistory.actions.replace')}
            />
          </div>
        </div>
      ) : (
        <FileDropzone
          file={file}
          onFileSelect={onFileSelect}
          disabled={disabled}
          accept=".pdf"
          prompt={t('repairHistory.fields.dropFile')}
        />
      )}
      {isUploading && <UploadProgressBar progress={uploadProgress} />}
    </FormSection>
  );
}

// UploadProgressBar renders a simple track filled to the current upload percentage.
function UploadProgressBar({ progress }: { progress: number }) {
  return (
    <div className="bg-border h-1.5 w-full overflow-hidden rounded-full">
      <div
        className="bg-primary h-full rounded-full transition-[width] duration-150"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

// collectChangedFields returns only the entry fields whose value differs from the saved entry,
// so the update call is skipped entirely when nothing changed.
function collectChangedFields(
  entry: RepairHistoryEntry,
  next: { name: string; date: string; state: RepairState }
): UpdateRepairRequest {
  const changed: UpdateRepairRequest = {};
  if (next.name !== entry.name) changed.name = next.name;
  if (next.date !== entry.date) changed.date = next.date;
  if (next.state !== entry.state) changed.state = next.state;
  return changed;
}
